import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import MaintenanceSchedulesCard from "../MaintenanceSchedules/MiniComponents/MaintenanceSchedulesCard";
import { getAllMyTasks } from "../../../redux/MaintenanceScheduleSlice/MaintenanceScheduleSlice";
import "./Tasks.css";

export default function Tasks() {
  const dispatch = useDispatch();
  const { allMyTasks } = useSelector((state) => state.maintenanceSchedule);

  // Local State
  const [allTasksData, setAllTasksData] = useState([]);
  const [displayedTasks, setDisplayedTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // جلب كل البيانات عند التحميل
  useEffect(() => {
    const fetchAllTasks = async () => {
      try {
        setLoading(true);

        // جلب أول صفحة
        const firstPage = await dispatch(getAllMyTasks(1)).unwrap();
        let allData = [...(firstPage.data || [])];

        const totalPagesCount = firstPage.pagination
          ? Math.ceil(firstPage.pagination.total / firstPage.pagination.limit)
          : 1;

        setTotalPages(totalPagesCount);

        // جلب باقي الصفحات
        if (totalPagesCount > 1) {
          const promises = [];
          for (let page = 2; page <= totalPagesCount; page++) {
            promises.push(dispatch(getAllMyTasks(page)).unwrap());
          }

          const otherPages = await Promise.all(promises);
          otherPages.forEach((pageData) => {
            if (pageData.data) {
              allData = [...allData, ...pageData.data];
            }
          });
        }

        console.log(`✅ Loaded all ${allData.length} tasks`);
        setAllTasksData(allData);
        setDisplayedTasks(allData);
      } catch (error) {
        console.error("❌ Error loading tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllTasks();
  }, [dispatch]);

  // البحث المحلي في الـ Frontend
  const searchInFrontend = useCallback((keyword, data) => {
    if (!keyword.trim()) {
      return data;
    }

    const searchTerm = keyword.toLowerCase().trim();

    return data.filter((task) => {
      const title = task.title?.toLowerCase() || "";
      const status = task.status?.toLowerCase() || "";
      const priority = task.priority?.toLowerCase() || "";
      const scheduleDate = task.schedule_date || "";
      const companyName =
        task.contractSchedule?.branch?.company?.name?.toLowerCase() || "";
      const branchName =
        task.contractSchedule?.branch?.name?.toLowerCase() || "";
      const contractNumber =
        task.contractSchedule?.contract_number?.toLowerCase() || "";
      const notes = task.notes?.toLowerCase() || "";

      return (
        title.includes(searchTerm) ||
        status.includes(searchTerm) ||
        priority.includes(searchTerm) ||
        scheduleDate.includes(searchTerm) ||
        companyName.includes(searchTerm) ||
        branchName.includes(searchTerm) ||
        contractNumber.includes(searchTerm) ||
        notes.includes(searchTerm)
      );
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      console.log("🔍 Frontend Search for:", keyword);

      if (keyword.trim() !== "") {
        setIsSearching(true);
        const results = searchInFrontend(keyword, allTasksData);
        console.log(`✅ Found ${results.length} results`);
        setDisplayedTasks(results);
      } else {
        setIsSearching(false);
        setDisplayedTasks(allTasksData);
      }
    }, 300),
    [allTasksData, searchInFrontend]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setDisplayedTasks(allTasksData);
  };

  // Handle pagination (مش هنحتاجه لأن كل البيانات محملة)
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="main">
      <section className="maintenance-items">
        <div>
          <h1 className="text-white">مهامي</h1>
        </div>
        <div className="container-fluid">
          <div className="row justify-content-between align-items-center mb-5 mt-3">
            {/* عدد المهام */}
            <div className="col-md-3 d-flex justify-content-md-start justify-content-center mb-3">
              <div className="card py-2 px-3">
                <p className="mb-0 primary-100">
                  {isSearching ? (
                    <>
                      نتائج البحث:{" "}
                      <span className="primary-100">
                        {displayedTasks.length}
                      </span>
                      <small
                        className="text-muted d-block"
                        style={{ fontSize: "0.8rem" }}
                      >
                        (من إجمالي {allTasksData.length})
                      </small>
                    </>
                  ) : (
                    <>
                      عدد المهام:{" "}
                      <span className="primary-100">
                        {allTasksData.length || 0}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Search Box */}
            <div className="col-md-5 text-center mb-3">
              <div className="search-box-tasks position-relative">
                <input
                  type="text"
                  placeholder="ابحث بالعنوان، الشركة، الفرع، الحالة، التاريخ..."
                  className="form-control search-input-tasks"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <i className="fas fa-search search-icon-tasks"></i>
                {searchQuery && (
                  <button
                    className="btn-clear-search-tasks"
                    onClick={clearSearch}
                    title="مسح البحث"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              {isSearching && (
                <div className="search-info-tasks text-center mt-2">
                  <small className="text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    وجد {displayedTasks.length} نتيجة للبحث عن "{searchQuery}"
                  </small>
                </div>
              )}
            </div>

            {/* مساحة فارغة للتوازن */}
            <div className="col-md-3 text-md-end text-center mb-3">
              {/* يمكن إضافة أزرار إضافية هنا لاحقاً */}
            </div>
          </div>

          {/* Loading Spinner */}
          {loading ? (
            <div className="loading-spinner-tasks">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
              <p className="text-center mt-3 text-muted">
                جاري تحميل جميع المهام...
              </p>
            </div>
          ) : (
            <>
              {/* Tasks Grid */}
              <div className="row">
                {displayedTasks && displayedTasks.length > 0 ? (
                  displayedTasks.map((maintenanceSchedule) => (
                    <div
                      className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-12 mb-3"
                      key={maintenanceSchedule.id}
                    >
                      <MaintenanceSchedulesCard
                        maintenanceSchedule={maintenanceSchedule}
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <i className="fas fa-tasks fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      {searchQuery
                        ? `لا توجد نتائج للبحث عن "${searchQuery}"`
                        : "لا توجد مهام مخصصة لك"}
                    </p>
                    {searchQuery && (
                      <button
                        className="btn btn-sm btn-primary mt-2"
                        onClick={clearSearch}
                      >
                        <i className="fas fa-times me-2"></i>
                        مسح البحث وعرض كل المهام
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Results Summary */}
              {displayedTasks.length > 0 && (
                <div className="results-summary-tasks text-center mt-4">
                  <p className="text-muted">
                    عرض {displayedTasks.length} مهمة
                    {isSearching && ` من إجمالي ${allTasksData.length}`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
